import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getSignedFileUrl } from '@/lib/storage';
import type { StakeholderAttachment } from '@/pages/admin/StakeholderManagement';

interface FileWithPreview {
  file: File;
  preview?: string;
  name: string;
  type: string;
  size: number;
  id: string;
}

interface StakeholderFileUploadProps {
  stakeholderId?: string;
  existingFiles?: StakeholderAttachment[];
  value?: FileWithPreview[];
  onFilesChange: (files: FileWithPreview[]) => void;
  onDeleteExisting?: (path: string) => void;
  disabled?: boolean;
  maxFiles?: number;
}

export function StakeholderFileUpload({
  stakeholderId,
  existingFiles = [],
  value = [],
  onFilesChange,
  onDeleteExisting,
  disabled = false,
  maxFiles = 10
}: StakeholderFileUploadProps) {
  const [newFiles, setNewFiles] = useState<FileWithPreview[]>(value);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loadingUrls, setLoadingUrls] = useState<Record<string, boolean>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setNewFiles(value);
  }, [value]);

  // ✅ PHASE 3: Fix memory leak - cleanup only on unmount
  const revokedUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    return () => {
      newFiles.forEach(file => {
        if (file.preview && !revokedUrls.current.has(file.preview)) {
          URL.revokeObjectURL(file.preview);
          revokedUrls.current.add(file.preview);
        }
      });
    };
  }, []); // ✅ Only cleanup on unmount

  // ✅ PHASE 2: Batch fetch signed URLs with refresh mechanism
  useEffect(() => {
    const fetchSignedUrls = async () => {
      const pending = existingFiles.filter(f => !signedUrls[f.path]);
      if (pending.length === 0) return;

      // Mark as loading
      const loadingState: Record<string, boolean> = {};
      pending.forEach(f => { loadingState[f.path] = true; });
      setLoadingUrls(prev => ({ ...prev, ...loadingState }));
      
      // Batch fetch in parallel (TTL 50 mins, leave 10 min buffer)
      const results = await Promise.allSettled(
        pending.map(f => getSignedFileUrl(f.path, 3000))
      );

      const newUrls: Record<string, string> = {};
      const newLoadingState: Record<string, boolean> = {};
      
      results.forEach((r, i) => {
        const file = pending[i];
        newLoadingState[file.path] = false;
        
        if (r.status === 'fulfilled') {
          newUrls[file.path] = r.value;
        } else {
          console.error(`Failed to load signed URL for ${file.path}:`, r.reason);
          toast.error(`Failed to load file: ${file.name}`);
        }
      });

      setSignedUrls(prev => ({ ...prev, ...newUrls }));
      setLoadingUrls(prev => ({ ...prev, ...newLoadingState }));
    };

    fetchSignedUrls();
  }, [existingFiles]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    const validFiles: FileWithPreview[] = [];
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 
      'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const maxSize = 5 * 1024 * 1024;

    const totalFiles = newFiles.length + existingFiles.length + selectedFiles.length;
    if (totalFiles > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed. You have ${newFiles.length + existingFiles.length} files, trying to add ${selectedFiles.length}.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not allowed: ${file.name}`);
        continue;
      }

      if (file.size > maxSize) {
        toast.error(`File too large (max 5MB): ${file.name}`);
        continue;
      }

      let preview: string | undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      validFiles.push({
        file,
        preview,
        name: file.name,
        type: file.type,
        size: file.size,
        id: `${file.name}-${file.lastModified}-${Date.now()}`
      });
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...newFiles, ...validFiles];
      setNewFiles(updatedFiles);
      onFilesChange(updatedFiles);
      if (import.meta.env.DEV) {
        console.debug(`[StakeholderFileUpload] Added ${validFiles.length} files`);
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemoveNew = (id: string) => {
    const fileToRemove = newFiles.find(f => f.id === id);
    if (fileToRemove?.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    const updatedFiles = newFiles.filter(f => f.id !== id);
    setNewFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const handleRemoveExisting = (path: string) => {
    if (onDeleteExisting) {
      onDeleteExisting(path);
    }
  };

  const renderThumbnail = (file: { type: string; preview?: string; url?: string; name: string }, isLoading: boolean = false) => {
    if (isLoading) {
      return (
        <div className="w-full h-24 flex items-center justify-center bg-muted">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (file.type.startsWith('image/')) {
      const src = file.preview || file.url;
      return src ? (
        <img src={src} alt={file.name} className="w-full h-24 object-cover" />
      ) : (
        <div className="w-full h-24 flex items-center justify-center bg-muted">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    return (
      <div className="w-full h-24 flex items-center justify-center bg-muted">
        <FileText className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Files
        </Button>
        <input
          ref={inputRef}
          id="stakeholder-file-input"
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx"
          className="hidden"
          onChange={handleFileSelect}
          disabled={disabled}
        />
        <span className="text-xs text-muted-foreground">
          {newFiles.length + existingFiles.length}/{maxFiles} files. Max 5MB per file. Images, PDF, DOC supported.
        </span>
      </div>

      {existingFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Existing Attachments ({existingFiles.length})</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {existingFiles.map((file) => (
              <div key={file.path} className="relative group border rounded-lg overflow-hidden">
                {renderThumbnail(
                  { ...file, url: signedUrls[file.path] },
                  loadingUrls[file.path]
                )}
                {onDeleteExisting && !disabled && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemoveExisting(file.path)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
                <div className="p-1 text-xs truncate bg-background/80" title={file.name}>
                  {file.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {newFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">New Files to Upload ({newFiles.length})</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {newFiles.map((file) => (
              <div key={file.id} className="relative group border rounded-lg overflow-hidden">
                {renderThumbnail(file)}
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemoveNew(file.id)}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
                <div className="p-1 text-xs truncate bg-background/80" title={file.name}>
                  {file.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
